import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Rating,
  Stack,
  Skeleton,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  DirectionsCar,
  Person,
  CheckCircle,
  Cancel,
  Warning,
  Schedule,
  Visibility,
  ExpandMore,
  Phone,
  Email,
  LocationOn,
  Description,
  PhotoCamera,
  Assignment,
  VerifiedUser,
  Error as ErrorIcon,
  TrendingUp,
  SailingOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-hot-toast';
import { adminApiService, type AdminDriver } from '@/services/adminApiService';
import { EnhancedDriverCard } from '@/components/drivers/EnhancedDriverCard';
import { vehicleApprovalService, type PendingVehicle } from '@/services/vehicleApprovalService';
import { VehicleApprovalCard } from '@/components/vehicles/VehicleApprovalCard';
import { yachtApprovalService, type PendingYacht } from '@/services/yachtApprovalService';
import { YachtApprovalCard } from '@/components/yachts/YachtApprovalCard';

interface Driver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  licenseNumber: string;
  licenseState: string;
  licenseExpiryDate: string;
  status: 'pending' | 'in_review' | 'approved' | 'rejected';
  documents: {
    id: string;
    type: 'license' | 'insurance' | 'registration' | 'background_check' | 'photo';
    status: 'pending' | 'approved' | 'rejected';
    uploadedAt: string;
  }[];
  backgroundCheckStatus: 'pending' | 'clear' | 'flag' | 'failed';
  createdAt: string;
  submittedAt?: string;
}

interface ApprovalStats {
  vehicles: {
    total: number;
    pending: number;
    incomplete: number;
    in_review: number;
    approved: number;
    rejected: number;
  };
  yachts: {
    total: number;
    pending: number;
    incomplete: number;
    in_review: number;
    approved: number;
    rejected: number;
  };
  drivers: {
    total: number;
    pending: number;
    in_review: number;
    approved: number;
    rejected: number;
  };
  hostDrivers: {
    total: number;
    pending: number;
    in_review: number;
    approved: number;
    rejected: number;
  };
}

type TabType = 'vehicles' | 'yachts' | 'drivers' | 'host-drivers';
type StatusFilter = 'all' | 'incomplete' | 'in_review' | 'pending_review' | 'approved' | 'rejected';

export default function ApprovalDashboardMUI() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>('vehicles'); // Start with vehicles tab
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending_review'); // Default to pending for faster load
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Advanced vehicle filters
  const [fuelTypeFilter, setFuelTypeFilter] = useState<'all' | 'petrol' | 'diesel' | 'electric' | 'hybrid'>('all');
  const [transmissionFilter, setTransmissionFilter] = useState<'all' | 'automatic' | 'manual'>('all');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<'all' | 'sedan' | 'suv' | 'truck' | 'van' | 'coupe' | 'convertible'>('all');
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [vehicles, setVehicles] = useState<PendingVehicle[]>([]);
  const [yachts, setYachts] = useState<PendingYacht[]>([]);
  const [drivers, setDrivers] = useState<AdminDriver[]>([]);
  const [hostDrivers, setHostDrivers] = useState<AdminDriver[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Get admin user ID from localStorage
  const getAdminUserId = () => {
    // For staging environment, use the hardcoded staging admin ID
    const currentEnv = localStorage.getItem('polarride_admin_environment');
    if (currentEnv === 'staging') {
      return '00000000-0000-0000-0000-000000000001';
    }

    // For production and dev, use the logged-in user's ID
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.id) {
          return user.id;
        }
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }

    // Fallback for dev environment
    return 'admin-default';
  };

  const [adminUserId] = useState(getAdminUserId());

  // Log admin user ID for debugging
  useEffect(() => {
  }, [adminUserId]);

  const [stats, setStats] = useState<ApprovalStats>({
    vehicles: { total: 0, pending: 0, incomplete: 0, in_review: 0, approved: 0, rejected: 0 },
    yachts: { total: 0, pending: 0, incomplete: 0, in_review: 0, approved: 0, rejected: 0 },
    drivers: { total: 0, pending: 0, in_review: 0, approved: 0, rejected: 0 },
    hostDrivers: { total: 0, pending: 0, in_review: 0, approved: 0, rejected: 0 }
  });

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [actionDialog, setActionDialog] = useState<{ open: boolean; action: 'approve' | 'reject' | null; notes: string }>({
    open: false,
    action: null,
    notes: ''
  });

  // Separate cache for drivers, host drivers, and vehicles
  const driversCacheRef = useRef<{
    data: AdminDriver[] | null;
    stats: ApprovalStats['drivers'] | null;
    timestamp: number;
    filters: string;
  }>({ data: null, stats: null, timestamp: 0, filters: '' });

  const hostDriversCacheRef = useRef<{
    data: AdminDriver[] | null;
    stats: ApprovalStats['hostDrivers'] | null;
    timestamp: number;
    filters: string;
  }>({ data: null, stats: null, timestamp: 0, filters: '' });

  const vehiclesCacheRef = useRef<{
    data: PendingVehicle[] | null;
    stats: ApprovalStats['vehicles'] | null;
    timestamp: number;
    filters: string;
  }>({ data: null, stats: null, timestamp: 0, filters: '' });

  const yachtsCacheRef = useRef<{
    data: PendingYacht[] | null;
    stats: ApprovalStats['yachts'] | null;
    timestamp: number;
    filters: string;
  }>({ data: null, stats: null, timestamp: 0, filters: '' });

  const CACHE_DURATION = 60000; // 60 seconds

  // Performance timing
  const startTimeRef = useRef<number>(0);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Separate effect to fetch data when filters or tab changes
  useEffect(() => {
    fetchApprovalData();
  }, [activeTab, statusFilter, debouncedSearchQuery]);

  // Optimized: Refresh single vehicle instead of all
  const refreshSingleVehicle = async (vehicleId: string) => {
    try {

      // Fetch just this vehicle (we need to get it from the full list)
      const response = await vehicleApprovalService.getPendingVehicles({ limit: 1000 });

      if (response.success) {
        const updatedVehicle = response.data.vehicles.find(v => v.id === vehicleId);

        if (updatedVehicle) {
          // Update just this vehicle in the state
          setVehicles(prev => prev.map(v => v.id === vehicleId ? updatedVehicle : v));
        } else {
        }
      }
    } catch (error) {
      console.error('Failed to refresh vehicle:', error);
      // Fallback to full refresh only on error
      await fetchApprovalData(true);
    }
  };

  // Optimized: Refresh single yacht instead of all
  const refreshSingleYacht = async (yachtId: string) => {
    try {

      const response = await yachtApprovalService.getPendingYachts({ limit: 1000 });

      if (response.success) {
        const updatedYacht = response.data.yachts.find(y => y.id === yachtId);

        if (updatedYacht) {
          setYachts(prev => prev.map(y => y.id === yachtId ? updatedYacht : y));
        }
      }
    } catch (error) {
      console.error('Failed to refresh yacht:', error);
      await fetchApprovalData(true);
    }
  };

  const fetchApprovalData = async (skipCache = false) => {
    const startTime = performance.now();

    setIsLoading(true);
    setError(null);

    // Cache key includes both search query AND statusFilter (since we now fetch filtered data)
    const cacheKey = `${debouncedSearchQuery}-${statusFilter}`;

    // OPTIMIZATION 1: Lazy load by tab - only fetch active tab data
    if (activeTab === 'drivers') {
      await fetchDriversData(skipCache, cacheKey);
    } else if (activeTab === 'host-drivers') {
      await fetchHostDriversData(skipCache, cacheKey);
    } else if (activeTab === 'yachts') {
      await fetchYachtsData(skipCache, cacheKey);
    } else {
      await fetchVehiclesData(skipCache, cacheKey);
    }

    const loadTime = ((performance.now() - startTime) / 1000).toFixed(2);

    setIsLoading(false);
  };

  const fetchDriversData = async (skipCache: boolean, cacheKey: string) => {
    // OPTIMIZATION 2: Stale-while-revalidate - Show cached data immediately, fetch in background
    const hasCache = driversCacheRef.current.data && driversCacheRef.current.filters === cacheKey;
    const cacheAge = Date.now() - driversCacheRef.current.timestamp;
    const isCacheFresh = cacheAge < CACHE_DURATION;

    if (!skipCache && hasCache) {

      // ALWAYS apply status filter to cached data (even if cache is fresh)
      const allCachedDrivers = driversCacheRef.current.data;
      let displayDrivers = [...allCachedDrivers]; // Create new array to ensure React detects change
      if (statusFilter !== 'all') {
        displayDrivers = allCachedDrivers.filter(d => {
          if (statusFilter === 'pending_review' || statusFilter === 'in_review') return d.status === 'pending_review';
          if (statusFilter === 'approved') return d.status === 'approved';
          if (statusFilter === 'rejected') return d.status === 'rejected';
          return true;
        });
      }

      setDrivers(displayDrivers);
      if (driversCacheRef.current.stats) {
        setStats(prev => ({ ...prev, drivers: driversCacheRef.current.stats! }));
      }
      setIsLoading(false); // Show cached data immediately

      // If cache is still fresh, don't fetch again
      if (isCacheFresh) {
        return;
      }
      // Otherwise, continue to fetch fresh data in background
    }

    try {
      // ALWAYS fetch ALL statuses to calculate overall stats (for stat cards at top)
      const response = await adminApiService.getDrivers({
        search: debouncedSearchQuery || undefined
      });

      if (response.success) {
        // Filter for independent drivers only
        const allDrivers = response.data.drivers.filter(d => !d.driverType || d.driverType === 'independent');

        // Calculate OVERALL stats (never change regardless of filters)
        const driverStats = {
          total: allDrivers.length,
          pending: allDrivers.filter(d => d.status === 'pending_review').length,
          in_review: allDrivers.filter(d => d.status === 'pending_review').length,
          approved: allDrivers.filter(d => d.status === 'approved').length,
          rejected: allDrivers.filter(d => d.status === 'rejected').length
        };

        // Now filter the display list based on statusFilter
        let displayDrivers = allDrivers;
        if (statusFilter !== 'all') {
          displayDrivers = allDrivers.filter(d => {
            if (statusFilter === 'pending_review' || statusFilter === 'in_review') return d.status === 'pending_review';
            if (statusFilter === 'approved') return d.status === 'approved';
            if (statusFilter === 'rejected') return d.status === 'rejected';
            return true;
          });
        }

        setDrivers(displayDrivers);
        setStats(prev => ({ ...prev, drivers: driverStats }));

        // Cache the results (cache ALL drivers, not just the filtered display)
        driversCacheRef.current = {
          data: allDrivers,
          stats: driverStats,
          timestamp: Date.now(),
          filters: cacheKey
        };
      } else {
        throw new Error('Failed to load drivers');
      }
    } catch (err) {
      console.error('❌ Driver API failed:', err);
      setDrivers([]);
      setStats(prev => ({ ...prev, drivers: { total: 0, pending: 0, in_review: 0, approved: 0, rejected: 0 } }));
      setError('Driver data unavailable');
    }
  };

  const fetchHostDriversData = async (skipCache: boolean, cacheKey: string) => {
    // OPTIMIZATION 2: Stale-while-revalidate - Show cached data immediately, fetch in background
    const hasCache = hostDriversCacheRef.current.data && hostDriversCacheRef.current.filters === cacheKey;
    const cacheAge = Date.now() - hostDriversCacheRef.current.timestamp;
    const isCacheFresh = cacheAge < CACHE_DURATION;

    if (!skipCache && hasCache) {

      // ALWAYS apply status filter to cached data (even if cache is fresh)
      const allCachedHostDrivers = hostDriversCacheRef.current.data;
      let displayHostDrivers = [...allCachedHostDrivers]; // Create new array to ensure React detects change
      if (statusFilter !== 'all') {
        displayHostDrivers = allCachedHostDrivers.filter(d => {
          if (statusFilter === 'pending_review' || statusFilter === 'in_review') return d.status === 'pending_review';
          if (statusFilter === 'approved') return d.status === 'approved';
          if (statusFilter === 'rejected') return d.status === 'rejected';
          return true;
        });
      }

      setHostDrivers(displayHostDrivers);
      if (hostDriversCacheRef.current.stats) {
        setStats(prev => ({ ...prev, hostDrivers: hostDriversCacheRef.current.stats! }));
      }
      setIsLoading(false); // Show cached data immediately

      // If cache is still fresh, don't fetch again
      if (isCacheFresh) {
        return;
      }
      // Otherwise, continue to fetch fresh data in background
    }

    try {
      // ALWAYS fetch ALL statuses to calculate overall stats (for stat cards at top)
      const response = await adminApiService.getHostDrivers({
        search: debouncedSearchQuery || undefined
      });

      if (response.success) {
        const allHostDrivers = response.data.drivers;

        // Calculate OVERALL stats (never change regardless of filters)
        const hostDriverStats = {
          total: allHostDrivers.length,
          pending: allHostDrivers.filter(d => d.status === 'pending_review').length,
          in_review: allHostDrivers.filter(d => d.status === 'pending_review').length,
          approved: allHostDrivers.filter(d => d.status === 'approved').length,
          rejected: allHostDrivers.filter(d => d.status === 'rejected').length
        };

        // Now filter the display list based on statusFilter
        let displayHostDrivers = allHostDrivers;
        if (statusFilter !== 'all') {
          displayHostDrivers = allHostDrivers.filter(d => {
            if (statusFilter === 'pending_review' || statusFilter === 'in_review') return d.status === 'pending_review';
            if (statusFilter === 'approved') return d.status === 'approved';
            if (statusFilter === 'rejected') return d.status === 'rejected';
            return true;
          });
        }

        setHostDrivers(displayHostDrivers);
        setStats(prev => ({ ...prev, hostDrivers: hostDriverStats }));

        // Cache the results (cache ALL host drivers, not just the filtered display)
        hostDriversCacheRef.current = {
          data: allHostDrivers,
          stats: hostDriverStats,
          timestamp: Date.now(),
          filters: cacheKey
        };
      } else {
        throw new Error('Failed to load host drivers');
      }
    } catch (err) {
      console.error('❌ Host Driver API failed:', err);
      setHostDrivers([]);
      setStats(prev => ({ ...prev, hostDrivers: { total: 0, pending: 0, in_review: 0, approved: 0, rejected: 0 } }));
      setError('Host driver data unavailable');
    }
  };

  const fetchVehiclesData = async (skipCache: boolean, cacheKey: string) => {
    // OPTIMIZATION 2: Stale-while-revalidate - Show cached data immediately, fetch in background
    const hasCache = vehiclesCacheRef.current.data && vehiclesCacheRef.current.filters === cacheKey;
    const cacheAge = Date.now() - vehiclesCacheRef.current.timestamp;
    const isCacheFresh = cacheAge < CACHE_DURATION;

    if (!skipCache && hasCache) {

      // Show cached data immediately
      setVehicles(vehiclesCacheRef.current.data);
      if (vehiclesCacheRef.current.stats) {
        setStats(prev => ({ ...prev, vehicles: vehiclesCacheRef.current.stats! }));
      }
      setIsLoading(false); // Show cached data immediately

      // If cache is still fresh, don't fetch again
      if (isCacheFresh) {
        return;
      }
      // Otherwise, continue to fetch fresh data in background
    }

    try {
      const startFetch = performance.now();

      // Map statusFilter to backend status value
      const backendStatus = statusFilter === 'all' ? undefined
        : statusFilter === 'incomplete' ? 'draft'
        : statusFilter === 'pending_review' || statusFilter === 'in_review' ? 'pending_approval'
        : statusFilter as any;

      console.log(`🔍 Fetching vehicles - statusFilter: ${statusFilter}, backendStatus: ${backendStatus}, cacheKey: ${cacheKey}`);

      // 🚀 OPTIMIZED: Use dual API call approach
      // 1. Fetch stats (fast, cached, counts only) - for stat cards
      // 2. Fetch filtered data (only the selected status) - for display list

      const [statsRes, dataRes] = await Promise.all([
        vehicleApprovalService.getApprovalStats({ search: debouncedSearchQuery || undefined }),
        vehicleApprovalService.getPendingVehicles({
          status: backendStatus,
          limit: 100,
          offset: 0,
          search: debouncedSearchQuery || undefined
        })
      ]);

      const fetchTime = ((performance.now() - startFetch) / 1000).toFixed(2);
      console.log(`✅ Vehicles fetched in ${fetchTime}s - statsRes.success: ${statsRes.success}, dataRes.success: ${dataRes.success}, vehicles: ${dataRes.data?.vehicles?.length || 0}`);

      // Update stats from the stats endpoint
      if (statsRes.success && statsRes.data) {
        const vehicleStats = {
          total: statsRes.data.total,
          pending: statsRes.data.pending_approval,
          incomplete: statsRes.data.draft,
          in_review: statsRes.data.pending_approval,
          approved: statsRes.data.approved,
          rejected: statsRes.data.rejected
        };
        setStats(prev => ({ ...prev, vehicles: vehicleStats }));

        // Cache stats
        vehiclesCacheRef.current.stats = vehicleStats;
      } else {
        console.warn('⚠️ Stats API failed, keeping previous stats');
      }

      // Update display list from the data endpoint
      if (dataRes.success) {
        const displayVehicles = dataRes.data.vehicles;
        setVehicles(displayVehicles);

        // Cache the results
        vehiclesCacheRef.current = {
          ...vehiclesCacheRef.current,
          data: displayVehicles,
          timestamp: Date.now(),
          filters: cacheKey
        };
      } else {
        console.error('❌ Data API failed:', dataRes.error);
        throw new Error(dataRes.error || 'Failed to load vehicles');
      }

    } catch (err) {
      console.error('❌ Vehicle API failed:', err);
      setVehicles([]);
      setStats(prev => ({ ...prev, vehicles: { total: 0, pending: 0, incomplete: 0, in_review: 0, approved: 0, rejected: 0 } }));
      setError('Vehicle data unavailable');
    }
  };

  const fetchYachtsData = async (skipCache: boolean, cacheKey: string) => {

    // OPTIMIZATION 2: Stale-while-revalidate - Show cached data immediately, fetch in background
    const hasCache = yachtsCacheRef.current.data && yachtsCacheRef.current.filters === cacheKey;
    const cacheAge = Date.now() - yachtsCacheRef.current.timestamp;
    const isCacheFresh = cacheAge < CACHE_DURATION;

    if (!skipCache && hasCache) {

      // ALWAYS apply status filter to cached data (even if cache is fresh)
      const allCachedYachts = yachtsCacheRef.current.data;
      let displayYachts = [...allCachedYachts]; // Create new array to ensure React detects change
      if (statusFilter !== 'all') {
        displayYachts = allCachedYachts.filter(y => {
          if (statusFilter === 'incomplete') return y.status === 'draft';
          if (statusFilter === 'pending_review' || statusFilter === 'in_review') return y.status === 'pending_approval';
          if (statusFilter === 'approved') return y.status === 'approved';
          if (statusFilter === 'rejected') return y.status === 'rejected';
          return true;
        });
      }

      setYachts(displayYachts);
      if (yachtsCacheRef.current.stats) {
        setStats(prev => ({ ...prev, yachts: yachtsCacheRef.current.stats! }));
      }
      setIsLoading(false); // Show cached data immediately

      // If cache is still fresh, don't fetch again
      if (isCacheFresh) {
        return;
      }
      // Otherwise, continue to fetch fresh data in background
    }

    try {
      const startFetch = performance.now();

      // ALWAYS fetch ALL statuses to calculate overall stats (for stat cards at top)
      const [draftRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
        yachtApprovalService.getPendingYachts({ status: 'draft', limit: 100, offset: 0 }),
        yachtApprovalService.getPendingYachts({ status: 'pending_approval', limit: 100, offset: 0 }),
        yachtApprovalService.getPendingYachts({ status: 'approved', limit: 100, offset: 0 }),
        yachtApprovalService.getPendingYachts({ status: 'rejected', limit: 100, offset: 0 })
      ]);

      const fetchTime = ((performance.now() - startFetch) / 1000).toFixed(2);

      // Combine ALL yachts from all statuses
      // Backend returns { success: true, data: { yachts: [...], pagination: {...} } }
      const allYachts = [
        ...(draftRes.success && draftRes.data?.yachts ? draftRes.data.yachts : []),
        ...(pendingRes.success && pendingRes.data?.yachts ? pendingRes.data.yachts : []),
        ...(approvedRes.success && approvedRes.data?.yachts ? approvedRes.data.yachts : []),
        ...(rejectedRes.success && rejectedRes.data?.yachts ? rejectedRes.data.yachts : [])
      ];

      // Calculate OVERALL stats (these never change regardless of filters)
      const yachtStats = {
        total: allYachts.length,
        pending: allYachts.filter(y => y.status === 'pending_approval').length,
        incomplete: allYachts.filter(y => y.status === 'draft').length,
        in_review: allYachts.filter(y => y.status === 'pending_approval').length,
        approved: allYachts.filter(y => y.status === 'approved').length,
        rejected: allYachts.filter(y => y.status === 'rejected').length
      };

      // Update stats cards (always show overall stats)
      setStats(prev => ({ ...prev, yachts: yachtStats }));

      // Now filter the display list based on statusFilter
      let displayYachts = allYachts;
      if (statusFilter !== 'all') {
        displayYachts = allYachts.filter(y => {
          if (statusFilter === 'incomplete') return y.status === 'draft';
          if (statusFilter === 'pending_review' || statusFilter === 'in_review') return y.status === 'pending_approval';
          if (statusFilter === 'approved') return y.status === 'approved';
          if (statusFilter === 'rejected') return y.status === 'rejected';
          return true;
        });
      }

      setYachts(displayYachts);

      // Update cache with ALL yachts (not just filtered)
      yachtsCacheRef.current = {
        data: allYachts,
        stats: yachtStats,
        timestamp: Date.now(),
        filters: cacheKey
      };

    } catch (err) {
      console.error('❌ Yacht API failed:', err);
      setYachts([]);
      setStats(prev => ({ ...prev, yachts: { total: 0, pending: 0, incomplete: 0, in_review: 0, approved: 0, rejected: 0 } }));
      setError('Yacht data unavailable');
    }
  };

  const loadMockDriverData = () => {
    // Show empty state instead of fake data
    setDrivers([]);
    setStats(prev => ({
      ...prev,
      drivers: {
        total: 0,
        pending: 0,
        in_review: 0,
        approved: 0,
        rejected: 0
      }
    }));
  };

  const handleApprove = async (id: string, notes: string) => {
    try {
      if (activeTab === 'vehicles') {
        // Vehicle approval using real API

        // Optimistic update
        setVehicles(prev => prev.map(v =>
          v.id === id ? { ...v, status: 'approved', review: { ...v.review, reviewedAt: new Date().toISOString() } } : v
        ));

        const response = await vehicleApprovalService.approveVehicle(id, adminUserId, notes);

        if (response.success) {
          toast.success('Vehicle approved successfully! ✅');

          // Update stats
          setStats(prev => ({
            ...prev,
            vehicles: {
              ...prev.vehicles,
              approved: prev.vehicles.approved + 1,
              in_review: Math.max(0, prev.vehicles.in_review - 1),
              pending: Math.max(0, prev.vehicles.pending - 1)
            }
          }));
          // Invalidate cache
          vehiclesCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
        } else {
          console.error('❌ Approval failed:', response.error);
          toast.error(response.error || 'Failed to approve vehicle');
          throw new Error(response.error || 'Failed to approve vehicle');
        }
      } else if (activeTab === 'yachts') {
        // Yacht approval using real API

        // Optimistic update
        setYachts(prev => prev.map(y =>
          y.id === id ? { ...y, status: 'approved', review: { ...y.review, reviewedAt: new Date().toISOString() } } : y
        ));

        const response = await yachtApprovalService.approveYacht(id, adminUserId, notes);

        if (response.success) {
          toast.success('Yacht approved successfully! ✅');

          // Update stats
          setStats(prev => ({
            ...prev,
            yachts: {
              ...prev.yachts,
              approved: prev.yachts.approved + 1,
              in_review: Math.max(0, prev.yachts.in_review - 1),
              pending: Math.max(0, prev.yachts.pending - 1)
            }
          }));
          // Invalidate cache
          yachtsCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
        } else {
          console.error('❌ Approval failed:', response.error);
          toast.error(response.error || 'Failed to approve yacht');
          throw new Error(response.error || 'Failed to approve yacht');
        }
      } else {
        // Driver or Host Driver approval using real API
        const isHostDriver = activeTab === 'host-drivers';

        // Optimistic update
        if (isHostDriver) {
          setHostDrivers(prev => prev.map(d =>
            d.id === id ? { ...d, status: 'approved' as const, canApprove: false, reviewedAt: new Date().toISOString() } : d
          ));
        } else {
          setDrivers(prev => prev.map(d =>
            d.id === id ? { ...d, status: 'approved' as const, canApprove: false, reviewedAt: new Date().toISOString() } : d
          ));
        }

        const response = isHostDriver
          ? await adminApiService.approveHostDriver(id, notes)
          : await adminApiService.approveDriver(id, notes);

        if (response.success) {
          toast.success(`${isHostDriver ? 'Host driver' : 'Driver'} approved successfully! ✅`);
          // Update stats
          if (isHostDriver) {
            setStats(prev => ({
              ...prev,
              hostDrivers: {
                ...prev.hostDrivers,
                approved: prev.hostDrivers.approved + 1,
                in_review: Math.max(0, prev.hostDrivers.in_review - 1),
                pending: Math.max(0, prev.hostDrivers.pending - 1)
              }
            }));
            // Invalidate cache
            hostDriversCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
          } else {
            setStats(prev => ({
              ...prev,
              drivers: {
                ...prev.drivers,
                approved: prev.drivers.approved + 1,
                in_review: Math.max(0, prev.drivers.in_review - 1),
                pending: Math.max(0, prev.drivers.pending - 1)
              }
            }));
            // Invalidate cache
            driversCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
          }
        } else {
          throw new Error(response.error || 'Failed to approve driver');
        }
      }
      setActionDialog({ open: false, action: null, notes: '' });
    } catch (error) {
      console.error('❌ Failed to approve:', error);
      setError(error instanceof Error ? error.message : 'Failed to approve');
      // Revert optimistic update on error
      await fetchApprovalData(true);
    }
  };

  // Handle yacht update (for visibility toggle, etc.)
  const handleYachtUpdate = (yachtId: string, updates: Partial<PendingYacht>) => {
    setYachts(prev => prev.map(y =>
      y.id === yachtId ? { ...y, ...updates } : y
    ));

    // Also update cache if it exists
    if (yachtsCacheRef.current.data) {
      yachtsCacheRef.current.data = yachtsCacheRef.current.data.map(y =>
        y.id === yachtId ? { ...y, ...updates } : y
      );
    }
  };

  const handleReject = async (id: string, reason: string) => {
    try {
      if (activeTab === 'vehicles') {
        // Vehicle rejection using real API
        // Optimistic update
        setVehicles(prev => prev.map(v =>
          v.id === id ? { ...v, status: 'rejected', review: { ...v.review, reviewedAt: new Date().toISOString(), rejectionReason: reason } } : v
        ));

        const response = await vehicleApprovalService.rejectVehicle(id, adminUserId, reason);

        if (response.success) {
          // Update stats
          setStats(prev => ({
            ...prev,
            vehicles: {
              ...prev.vehicles,
              rejected: prev.vehicles.rejected + 1,
              in_review: Math.max(0, prev.vehicles.in_review - 1),
              pending: Math.max(0, prev.vehicles.pending - 1)
            }
          }));
          // Invalidate cache
          vehiclesCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
        } else {
          throw new Error(response.error || 'Failed to reject vehicle');
        }
      } else if (activeTab === 'yachts') {
        // Yacht rejection using real API
        // Optimistic update
        setYachts(prev => prev.map(y =>
          y.id === id ? { ...y, status: 'rejected', review: { ...y.review, reviewedAt: new Date().toISOString(), rejectionReason: reason } } : y
        ));

        const response = await yachtApprovalService.rejectYacht(id, adminUserId, reason);

        if (response.success) {
          // Update stats
          setStats(prev => ({
            ...prev,
            yachts: {
              ...prev.yachts,
              rejected: prev.yachts.rejected + 1,
              in_review: Math.max(0, prev.yachts.in_review - 1),
              pending: Math.max(0, prev.yachts.pending - 1)
            }
          }));
          // Invalidate cache
          yachtsCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
        } else {
          throw new Error(response.error || 'Failed to reject yacht');
        }
      } else {
        // Driver or Host Driver rejection using real API
        const isHostDriver = activeTab === 'host-drivers';

        // Optimistic update
        if (isHostDriver) {
          setHostDrivers(prev => prev.map(d =>
            d.id === id ? { ...d, status: 'rejected' as const, canApprove: false, reviewedAt: new Date().toISOString() } : d
          ));
        } else {
          setDrivers(prev => prev.map(d =>
            d.id === id ? { ...d, status: 'rejected' as const, canApprove: false, reviewedAt: new Date().toISOString() } : d
          ));
        }

        const response = isHostDriver
          ? await adminApiService.rejectHostDriver(id, reason)
          : await adminApiService.rejectDriver(id, reason);

        if (response.success) {
          toast.success(`${isHostDriver ? 'Host driver' : 'Driver'} rejected successfully`);
          // Update stats
          if (isHostDriver) {
            setStats(prev => ({
              ...prev,
              hostDrivers: {
                ...prev.hostDrivers,
                rejected: prev.hostDrivers.rejected + 1,
                in_review: Math.max(0, prev.hostDrivers.in_review - 1),
                pending: Math.max(0, prev.hostDrivers.pending - 1)
              }
            }));
            // Invalidate cache
            hostDriversCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
          } else {
            setStats(prev => ({
              ...prev,
              drivers: {
                ...prev.drivers,
                rejected: prev.drivers.rejected + 1,
                in_review: Math.max(0, prev.drivers.in_review - 1),
                pending: Math.max(0, prev.drivers.pending - 1)
              }
            }));
            // Invalidate cache
            driversCacheRef.current = { data: null, stats: null, timestamp: 0, filters: '' };
          }
        } else {
          throw new Error(response.error || 'Failed to reject driver');
        }
      }
      setActionDialog({ open: false, action: null, notes: '' });
    } catch (error) {
      console.error('❌ Failed to reject:', error);
      setError(error instanceof Error ? error.message : 'Failed to reject');
      // Revert optimistic update on error
      await fetchApprovalData(true);
    }
  };

  // OPTIMIZATION 3: Memoize filtered arrays to avoid recalculation on every render
  const filteredVehicles = useMemo(() => {

    // Status and search filtering already done by backend API
    // Only apply advanced filters (fuel type, transmission, vehicle type) here
    const filtered = vehicles.filter(vehicle => {
      // Advanced filters
      const matchesFuelType = fuelTypeFilter === 'all' ||
        vehicle.vehicle.fuelType?.toLowerCase() === fuelTypeFilter.toLowerCase() ||
        (fuelTypeFilter === 'petrol' && vehicle.vehicle.fuelType?.toLowerCase() === 'gasoline');

      const matchesTransmission = transmissionFilter === 'all' ||
        vehicle.vehicle.transmission?.toLowerCase() === transmissionFilter.toLowerCase();

      const matchesVehicleType = vehicleTypeFilter === 'all' ||
        vehicle.vehicle.type?.toLowerCase() === vehicleTypeFilter.toLowerCase();

      return matchesFuelType && matchesTransmission && matchesVehicleType;
    });

    return filtered;
  }, [vehicles, fuelTypeFilter, transmissionFilter, vehicleTypeFilter]);

  const filteredYachts = useMemo(() => {

    // Status filtering already done in fetchYachtsData, apply search filter here
    const filtered = yachts.filter(yacht => {
      // Search filter match - search across multiple fields
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        `${yacht.yacht?.name || ''} ${yacht.yacht?.manufacturer || ''} ${yacht.yacht?.model || ''} ${yacht.yacht?.registrationNumber || ''} ${yacht.host?.name || ''} ${yacht.host?.email || ''} ${yacht.host?.phone || ''}`
          .toLowerCase().includes(searchLower);

      return matchesSearch;
    });

    return filtered;
  }, [yachts, searchQuery]);

  const filteredDrivers = useMemo(() => {
    // Status filtering already done in fetchDriversData, only apply search filter here
    return drivers.filter(driver => {
      const matchesSearch = !searchQuery ||
        `${driver.userName} ${driver.userEmail || ''} ${driver.license?.number || ''}`
          .toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [drivers, searchQuery]);

  const filteredHostDrivers = useMemo(() => {
    // Status filtering already done in fetchHostDriversData, only apply search filter here
    return hostDrivers.filter(driver => {
      const matchesSearch = !searchQuery ||
        `${driver.userName} ${driver.userEmail || ''} ${driver.license?.number || ''}`
          .toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [hostDrivers, searchQuery]);

  // Loading Skeleton Component
  const DriverCardSkeleton = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Skeleton variant="circular" width={64} height={64} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 3 }} />
      </CardContent>
    </Card>
  );

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {value}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const DriverCard = ({ driver }: { driver: AdminDriver }) => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'approved': return 'success';
        case 'pending_review': return 'warning';
        case 'rejected': return 'error';
        case 'draft': return 'default';
        default: return 'default';
      }
    };

    const getStatusLabel = (status: string) => {
      switch (status) {
        case 'pending_review': return 'Pending Review';
        case 'approved': return 'Approved';
        case 'rejected': return 'Rejected';
        case 'draft': return 'Draft';
        default: return status;
      }
    };

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                <Person />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {driver.userName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {driver.identity.nin} • License: {driver.license.number}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Expires: {new Date(driver.license.expiryDate).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={getStatusLabel(driver.status)}
              color={getStatusColor(driver.status) as any}
              size="small"
            />
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Completion</Typography>
              <Typography variant="body2">{driver.completionPercentage}%</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Documents</Typography>
              <Typography variant="body2">{driver.documents?.length || 0} uploaded</Typography>
            </Grid>
          </Grid>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Progress</Typography>
              <Typography variant="caption">{driver.completionPercentage}%</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={driver.completionPercentage} 
              sx={{ height: 6, borderRadius: 3 }}
              color={driver.completionPercentage === 100 ? 'success' : 'primary'}
            />
          </Box>

          {driver.documents && driver.documents.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Document Status:
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                {driver.documents.map((doc, index) => (
                  <Chip
                    key={index}
                    label={doc.type.replace('_', ' ')}
                    size="small"
                    color={doc.status === 'approved' ? 'success' : doc.status === 'rejected' ? 'error' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {driver.canApprove && (driver.status === 'pending_review' || driver.status === 'pending_verification' || driver.status === 'under_review') && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircle />}
                onClick={() => {
                  setSelectedItem(driver.id);
                  setActionDialog({ open: true, action: 'approve', notes: '' });
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Cancel />}
                onClick={() => {
                  setSelectedItem(driver.id);
                  setActionDialog({ open: true, action: 'reject', notes: '' });
                }}
              >
                Reject
              </Button>
              <IconButton size="small">
                <Visibility />
              </IconButton>
            </Box>
          )}
          
          {driver.submittedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Submitted: {new Date(driver.submittedAt).toLocaleDateString()}
              {driver.reviewedAt && ` • Reviewed: ${new Date(driver.reviewedAt).toLocaleDateString()}`}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Approvals & Registration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve vehicle listings and driver registrations
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchApprovalData}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>API Connection Issue:</strong> {error}. Some data may be using cached/fallback information.
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total"
            value={activeTab === 'vehicles' ? stats.vehicles.total : activeTab === 'host-drivers' ? stats.hostDrivers.total : stats.drivers.total}
            icon={activeTab === 'vehicles' ? <DirectionsCar /> : <Person />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="In Review"
            value={activeTab === 'vehicles' ? stats.vehicles.in_review : activeTab === 'host-drivers' ? stats.hostDrivers.in_review : stats.drivers.in_review}
            icon={<Schedule />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved"
            value={activeTab === 'vehicles' ? stats.vehicles.approved : activeTab === 'host-drivers' ? stats.hostDrivers.approved : stats.drivers.approved}
            icon={<CheckCircle />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Rejected"
            value={activeTab === 'vehicles' ? stats.vehicles.rejected : activeTab === 'host-drivers' ? stats.hostDrivers.rejected : stats.drivers.rejected}
            icon={<Cancel />}
            color="error.main"
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab
                label={`Vehicle Listings (${vehicles.length})`}
                value="vehicles"
                icon={<DirectionsCar />}
                iconPosition="start"
              />
              <Tab
                label={`Yacht Listings (${yachts.length})`}
                value="yachts"
                icon={<SailingOutlined />}
                iconPosition="start"
              />
              <Tab
                label={`Driver Registrations (${drivers.length})`}
                value="drivers"
                icon={<Person />}
                iconPosition="start"
              />
              <Tab
                label={`Host Driver Registrations (${hostDrivers.length})`}
                value="host-drivers"
                icon={<Person />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Filters */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder={`Search ${activeTab}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="incomplete">Incomplete</MenuItem>
                    <MenuItem value="pending_review">Pending Review</MenuItem>
                    <MenuItem value="in_review">In Review</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {activeTab === 'vehicles' && (
                <Grid item xs={12} md={3}>
                  <Badge
                    badgeContent={[fuelTypeFilter, transmissionFilter, vehicleTypeFilter].filter(f => f !== 'all').length}
                    color="primary"
                  >
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={() => setAdvancedFiltersOpen(true)}
                      fullWidth
                    >
                      Advanced Filters
                    </Button>
                  </Badge>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Grid container spacing={3}>
                {[1, 2, 3].map((i) => (
                  <Grid item xs={12} key={i}>
                    <DriverCardSkeleton />
                  </Grid>
                ))}
              </Grid>
            ) : activeTab === 'vehicles' ? (
              <Grid container spacing={3}>
                {filteredVehicles.length > 0 ? (
                  filteredVehicles.map(vehicle => (
                    <Grid item xs={12} lg={6} key={vehicle.id}>
                      <VehicleApprovalCard
                        vehicle={vehicle}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onPricingUpdate={() => refreshSingleVehicle(vehicle.id)}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 6, textAlign: 'center' }}>
                      <DirectionsCar sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No vehicles found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No vehicles match your current filters.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            ) : activeTab === 'yachts' ? (
              <Grid container spacing={3}>
                {filteredYachts.length > 0 ? (
                  filteredYachts.map(yacht => (
                    <Grid item xs={12} lg={6} key={yacht.id}>
                      <YachtApprovalCard
                        yacht={yacht}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onPricingUpdate={() => refreshSingleYacht(yacht.id)}
                        onYachtUpdate={handleYachtUpdate}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 6, textAlign: 'center' }}>
                      <SailingOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No yachts found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No yachts match your current filters.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            ) : activeTab === 'host-drivers' ? (
              <Grid container spacing={3}>
                {filteredHostDrivers.length > 0 ? (
                  filteredHostDrivers.map(driver => (
                    <Grid item xs={12} key={driver.id}>
                      <EnhancedDriverCard
                        driver={driver}
                        onApprove={handleApprove}
                        onReject={handleReject}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 6, textAlign: 'center' }}>
                      <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No host drivers found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No host drivers match your current filters.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Grid container spacing={3}>
                {filteredDrivers.length > 0 ? (
                  filteredDrivers.map(driver => (
                    <Grid item xs={12} key={driver.id}>
                      <EnhancedDriverCard
                        driver={driver}
                        onApprove={handleApprove}
                        onReject={handleReject}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 6, textAlign: 'center' }}>
                      <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No drivers found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No drivers match your current filters.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onClose={() => setActionDialog({ open: false, action: null, notes: '' })}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {actionDialog.action === 'approve' ? 'Approve' : 'Reject'} {activeTab === 'vehicles' ? 'Vehicle' : activeTab === 'yachts' ? 'Yacht' : activeTab === 'host-drivers' ? 'Host Driver' : 'Driver'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionDialog.action === 'approve'
              ? `Are you sure you want to approve this ${activeTab === 'vehicles' ? 'vehicle listing' : activeTab === 'yachts' ? 'yacht listing' : activeTab === 'host-drivers' ? 'host driver registration' : 'driver registration'}?`
              : `Please provide a reason for rejecting this ${activeTab === 'vehicles' ? 'vehicle listing' : activeTab === 'yachts' ? 'yacht listing' : activeTab === 'host-drivers' ? 'host driver registration' : 'driver registration'}.`
            }
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionDialog.action === 'approve' ? 'Notes (Optional)' : 'Rejection Reason *'}
            value={actionDialog.notes}
            onChange={(e) => setActionDialog(prev => ({ ...prev, notes: e.target.value }))}
            placeholder={actionDialog.action === 'approve' 
              ? 'Add any notes or instructions...' 
              : 'Explain why this is being rejected...'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: null, notes: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.action === 'approve' ? 'success' : 'error'}
            onClick={() => {
              if (selectedItem) {
                if (actionDialog.action === 'approve') {
                  handleApprove(selectedItem, actionDialog.notes);
                } else {
                  handleReject(selectedItem, actionDialog.notes);
                }
              }
            }}
            disabled={actionDialog.action === 'reject' && !actionDialog.notes.trim()}
          >
            {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advanced Filters Dialog */}
      <Dialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Advanced Vehicle Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Fuel Type Filter */}
            <FormControl fullWidth>
              <InputLabel>Fuel Type</InputLabel>
              <Select
                value={fuelTypeFilter}
                label="Fuel Type"
                onChange={(e) => setFuelTypeFilter(e.target.value as any)}
              >
                <MenuItem value="all">All Fuel Types</MenuItem>
                <MenuItem value="petrol">Petrol/Gasoline</MenuItem>
                <MenuItem value="diesel">Diesel</MenuItem>
                <MenuItem value="electric">Electric</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>

            {/* Transmission Filter */}
            <FormControl fullWidth>
              <InputLabel>Transmission</InputLabel>
              <Select
                value={transmissionFilter}
                label="Transmission"
                onChange={(e) => setTransmissionFilter(e.target.value as any)}
              >
                <MenuItem value="all">All Transmissions</MenuItem>
                <MenuItem value="automatic">Automatic</MenuItem>
                <MenuItem value="manual">Manual</MenuItem>
              </Select>
            </FormControl>

            {/* Vehicle Type Filter */}
            <FormControl fullWidth>
              <InputLabel>Vehicle Type</InputLabel>
              <Select
                value={vehicleTypeFilter}
                label="Vehicle Type"
                onChange={(e) => setVehicleTypeFilter(e.target.value as any)}
              >
                <MenuItem value="all">All Vehicle Types</MenuItem>
                <MenuItem value="sedan">Sedan</MenuItem>
                <MenuItem value="suv">SUV</MenuItem>
                <MenuItem value="truck">Truck</MenuItem>
                <MenuItem value="van">Van</MenuItem>
                <MenuItem value="coupe">Coupe</MenuItem>
                <MenuItem value="convertible">Convertible</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFuelTypeFilter('all');
              setTransmissionFilter('all');
              setVehicleTypeFilter('all');
            }}
          >
            Clear All
          </Button>
          <Button onClick={() => setAdvancedFiltersOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => setAdvancedFiltersOpen(false)}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}